import React from 'react';

import { Button, Chip, Tooltip, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { ActionLibraryInstance, InstanceCommentPurpose } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';

const PURPOSE_DELEGATION: InstanceCommentPurpose = 2;
const PURPOSE_COMPLETION: InstanceCommentPurpose = 3;

const getPurposeMessageKey = (purpose: InstanceCommentPurpose | undefined) => {
  if (purpose === PURPOSE_DELEGATION) return action_library_messages.instance_actions.comment_purpose_delegation;
  if (purpose === PURPOSE_COMPLETION) return action_library_messages.instance_actions.comment_purpose_completion;
  return action_library_messages.instance_actions.comment_purpose_general;
};

type InstanceCommentsSectionProps = {
  instance: ActionLibraryInstance;
  onOpenComments: () => void;
};

/** Renders the comments column: "See all comments (n)" text link + last comment preview. No Add button (only in modal). */
const InstanceCommentsSection: React.FC<InstanceCommentsSectionProps> = ({ instance, onOpenComments }) => {
  const { t } = useTranslation();
  const count = instance.instanceCommentsCount ?? 0;
  const lastComment = instance.lastInstanceComment;

  let displayText = '';
  if (lastComment) {
    const hasPreview = lastComment.textPreview !== undefined && lastComment.textPreview !== '';
    displayText = (hasPreview ? lastComment.textPreview : lastComment.text) ?? '';
  }
  const showTooltip = Boolean(lastComment?.isTruncated && lastComment?.text && lastComment.text !== displayText);
  const purposeLabel = lastComment?.purpose != null ? t(getPurposeMessageKey(lastComment.purpose)) : null;
  const isPurposeSpecial = lastComment?.purpose === PURPOSE_DELEGATION || lastComment?.purpose === PURPOSE_COMPLETION;

  return (
    <div style={{ backgroundColor: '#f9f9f9', padding: '10px 16px' }}>
      {lastComment ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
            {isPurposeSpecial && purposeLabel && <Chip color='primary' label={purposeLabel} size='small' variant='outlined' />}
            <Typography color='textSecondary' variant='caption'>
              {lastComment.userFullName || lastComment.userEmail || '—'} · {format(new Date(lastComment.createdAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
          </div>
          {showTooltip ? (
            <Tooltip title={lastComment.text}>
              <Typography component='span' style={{ cursor: 'help' }} variant='body2'>
                {displayText}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2'>{displayText}</Typography>
          )}
        </div>
      ) : (
        <Typography color='textSecondary' style={{ marginTop: 4 }} variant='body2'>
          {t(action_library_messages.instance_actions.instance_comments_empty)}
        </Typography>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Button
          aria-label={String(t(action_library_messages.instance_actions.see_all_comments))}
          color='primary'
          onClick={onOpenComments}
          size='small'
          style={{ textTransform: 'none', padding: 0, paddingTop: 4, minWidth: 0 }}
          variant='text'
        >
          {t(action_library_messages.instance_actions.see_all_comments)} ({count})
        </Button>
        <Button
          aria-label={String(t(action_library_messages.instance_actions.see_all_comments))}
          color='primary'
          onClick={onOpenComments}
          size='small'
          style={{ textTransform: 'none', padding: 0, paddingTop: 4, minWidth: 0 }}
          variant='text'
        >
          Add new
        </Button>
      </div>
    </div>
  );
};

export default InstanceCommentsSection;
